import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button as MuiButton,
  LinearProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSession } from '@hooks/useSession';
import { useOpportunity } from '@hooks/useOpportunity';
import { useStats } from '@hooks/useStats';
import { SessionTimer } from '@components/observation/SessionTimer';
import { OpportunityRecorder } from '@components/observation/OpportunityRecorder';
import { OpportunityList } from '@components/observation/OpportunityList';
import { Loading } from '@components/common/Loading';
import { Snackbar } from '@components/common/Snackbar';
import { useSessionNavigationStore } from '@store/session-navigation-store';
import { logger } from '@utils/logger';

export function ActiveObservationPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { session, loading: sessionLoading } = useSession(sessionId);
  const {
    opportunities,
    loading: oppLoading,
    createOpportunity,
    deleteOpportunity,
    reload,
  } = useOpportunity(sessionId);
  const stats = useStats(opportunities);

  const {
    pendingRecorderOpen,
    setActiveSessionId,
    clearActiveSession,
    setShowExitConfirmation,
    setPendingRecorderOpen,
  } = useSessionNavigationStore();

  const [recorderOpen, setRecorderOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Register this session as active in the navigation store
  useEffect(() => {
    if (sessionId && session && session.status === 'in_progress') {
      setActiveSessionId(sessionId);
    }
  }, [sessionId, session, setActiveSessionId]);

  useEffect(() => {
    if (session && session.status === 'completed') {
      clearActiveSession();
      navigate(`/session/${sessionId}/review`);
    }
  }, [session, sessionId, navigate, clearActiveSession]);

  // Open recorder if navigated here with pending flag from SessionFloatingActions
  useEffect(() => {
    if (pendingRecorderOpen && session && !sessionLoading) {
      setRecorderOpen(true);
      setPendingRecorderOpen(false);
    }
  }, [pendingRecorderOpen, session, sessionLoading, setPendingRecorderOpen]);

  // Listen for custom event from SessionFloatingActions to open recorder
  useEffect(() => {
    const handleOpenRecorder = () => setRecorderOpen(true);
    window.addEventListener('open-opportunity-recorder', handleOpenRecorder);
    return () => window.removeEventListener('open-opportunity-recorder', handleOpenRecorder);
  }, []);

  // Browser back button / beforeunload handling
  useEffect(() => {
    if (!session || session.status !== 'in_progress') return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    const handlePopState = () => {
      setShowExitConfirmation(true);
      window.history.pushState(null, '', window.location.href);
    };

    window.history.pushState(null, '', window.location.href);

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [session, setShowExitConfirmation]);

  const recentCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    opportunities.forEach((opp) => {
      counts[opp.observedHCWCode] = (counts[opp.observedHCWCode] || 0) + 1;
    });
    return counts;
  }, [opportunities]);

  const nextOpportunityNumber = useMemo(() => {
    if (opportunities.length === 0) return 1;
    const maxNumber = Math.max(...opportunities.map((o) => o.opportunityNumber));
    return maxNumber + 1;
  }, [opportunities]);

  const nextSequenceOrder = useMemo(() => {
    if (opportunities.length === 0) return 1;
    const maxOrder = Math.max(...opportunities.map((o) => o.sequenceOrder));
    return maxOrder + 1;
  }, [opportunities]);

  const handleSaveOpportunity = async (
    opportunity: Parameters<typeof createOpportunity>[0]
  ) => {
    try {
      await createOpportunity(opportunity);
      setSuccess(t('observation.opportunityRecorded'));
      if (reload) reload();
    } catch (err) {
      logger.error('Failed to save opportunity:', err);
      setError(t('observation.failedSave'));
    }
  };

  const handleDeleteOpportunity = async (opportunityId: string) => {
    try {
      await deleteOpportunity(opportunityId);
      setSuccess(t('observation.opportunityDeleted'));
    } catch (err) {
      logger.error('Failed to delete opportunity:', err);
      setError(t('observation.failedDelete'));
    }
  };


  if (sessionLoading || oppLoading) {
    return <Loading message={t('observation.loadingSession')} />;
  }

  if (!session) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error">
            {t('observation.sessionNotFound')}
          </Typography>
          <MuiButton onClick={() => navigate('/')} sx={{ mt: 2 }}>
            {t('common.goHome')}
          </MuiButton>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <SessionTimer startTime={session.startTime} />
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('observation.sessionProgress')}
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('observation.complianceRate')}: {stats.complianceRate.toFixed(1)}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={stats.complianceRate}
            sx={{ mt: 1, height: 8, borderRadius: 4 }}
            color={
              stats.complianceRate >= 80
                ? 'success'
                : stats.complianceRate >= 50
                ? 'warning'
                : 'error'
            }
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {t('observation.totalOpportunities')}: {stats.totalOpportunities}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('observation.actionsPerformed')}: {stats.actionsPerformed} | {t('observation.missed')}: {stats.actionsMissed}
        </Typography>
      </Paper>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('observation.recordedOpportunities')}
        </Typography>
        <OpportunityList
          opportunities={opportunities}
          onDelete={handleDeleteOpportunity}
        />
      </Box>

      <OpportunityRecorder
        open={recorderOpen}
        onClose={() => setRecorderOpen(false)}
        onSave={handleSaveOpportunity}
        sessionId={sessionId!}
        recentCounts={recentCounts}
        nextOpportunityNumber={nextOpportunityNumber}
        nextSequenceOrder={nextSequenceOrder}
      />

      <Snackbar
        open={!!error}
        message={error || ''}
        severity="error"
        onClose={() => setError(null)}
      />
      <Snackbar
        open={!!success}
        message={success || ''}
        severity="success"
        onClose={() => setSuccess(null)}
      />
    </Container>
  );
}
